import { useDisclosure } from '@chakra-ui/react';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useCallback } from 'react';

import Box from '~/common/components/Box';
import Button from '~/common/components/Buttons/Button/index';
import Modal from '~/common/components/Modals/Modal';
import ModalBody from '~/common/components/Modals/ModalBody';
import ModalCloseButton from '~/common/components/Modals/ModalCloseButton';
import ModalContent from '~/common/components/Modals/ModalContent';
import ModalFooter from '~/common/components/Modals/ModalFooter';
import ModalHeader from '~/common/components/Modals/ModalHeader';
import ModalOverlay from '~/common/components/Modals/ModalOverlay';
import Text from '~/common/components/Texts/Text';
import { useUser } from '~/common/hooks/useAuthentication';
import { useSelectedAlert } from '~/stores/navigationStore';
import theme from '~/theme';

import {
  StyledRemoveDocsLink,
  StyledTrashIcon,
} from './StyledComponents/ClassifyFormContent.styles';
import { useRemoveDocumentMutation } from './hooks/mutations';

const ClassifyHideDocsModal = () => {
  const { role: userRole } = useUser();
  const selectedAlert = useSelectedAlert();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { mutate, reset, status } = useRemoveDocumentMutation();

  const removeDoc = useCallback(() => {
    mutate({
      documentId: selectedAlert.ruleReference.id,
      documentTypeId: Number.parseInt(
        selectedAlert.additionalReferences.DocumentTypeId,
      ),
    });
    onClose();
  }, [selectedAlert, mutate, onClose]);

  const openHideDocsModal = () => {
    reset();
    onOpen();
  };

  return (
    <>
      {!userRole.length && (
        <StyledRemoveDocsLink
          id="hideDocLink"
          data-testid="hideDocLink"
          onClick={() => openHideDocsModal()}
        >
          Remove Document <StyledTrashIcon icon={faTrash} />
        </StyledRemoveDocsLink>
      )}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent p={6}>
          <ModalHeader
            p="unset"
            mb="24px"
            mr="24px"
            fontSize="1rem"
            fontWeight="500"
          >
            Are you sure you want to remove this document from BOLT?
          </ModalHeader>
          <ModalCloseButton
            data-testid="hideDocModalClose"
            top="15px"
            right="14px"
          />
          <ModalBody p="unset">
            <Text
              fontSize={theme.functions?.pixelsToRem(14)}
              textAlign="center"
            >
              By clicking yes, this document will no longer appear in the BOLT
              application.
            </Text>
          </ModalBody>
          <ModalFooter p="unset" mt="24px" flexDirection="column" gap="8px">
            <Box justifyContent="space-between" display="flex" width="100%">
              <Button
                variant="outline"
                borderColor="blue.600"
                color="gray.900"
                onClick={onClose}
                isDisabled={status === 'loading'}
                data-testid="hideDocModalNo"
                id="hideDocModalNo"
                data-tracking={`keptDocumentId-${selectedAlert?.ruleReference.id}`}
              >
                No, keep document
              </Button>
              <Button
                backgroundColor="blue.600"
                color="white"
                _hover={{
                  backgroundColor: 'blue.500',
                }}
                isDisabled={status === 'loading'}
                onClick={removeDoc}
                data-testid="hideDocModalYes"
                id="hideDocModalYes"
                data-tracking={`removedDocumentId-${selectedAlert?.ruleReference.id}`}
              >
                Yes, remove document
              </Button>
            </Box>
            {status === 'error' && (
              <Text color="red.500" fontSize="0.875rem">
                Error removing document. Please try again.
              </Text>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ClassifyHideDocsModal;
